import { trTR } from '@mui/material/locale';
import { createTheme, type Theme } from '@mui/material/styles';
import { alpha, common, fontFamily, GREY_CHANNEL_500, grey, palette } from './tokens.js';

declare module '@mui/material/styles' {
  interface TypeBackground {
    neutral: string;
  }

  // The Minimal kit's ramps carry two extra stops beyond MUI's default
  // light/main/dark, and screens use them for tints and deep accents.
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}

const divider = alpha(GREY_CHANNEL_500, 0.2);

const PRIMARY_CHANNEL_DARK = '0 120 103';

const SOFT_CHIP_COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

const TOGGLE_BUTTON_COLORS = SOFT_CHIP_COLORS;

const TOGGLE_BUTTON_SIZES = {
  small: { '--size': '40px', '--padding': '7px' },
  medium: { '--size': '48px', '--padding': '11px' },
  large: { '--size': '56px', '--padding': '15px' },
} as const;

const TOGGLE_BUTTON_GROUP_GAP = { '--group-gap': '4px' } as const;

/** The ramps are hex, while the kit's soft tints are a channel plus opacity. */
function alphaHex(hex: string, opacity: number): string {
  const red = Number.parseInt(hex.slice(1, 3), 16);
  const green = Number.parseInt(hex.slice(3, 5), 16);
  const blue = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${red} ${green} ${blue} / ${opacity})`;
}

export function createBtsisTheme(mode: 'light' | 'dark' = 'light'): Theme {
  const isLight = mode === 'light';

  // The core trTR locale is passed as a second argument because MUI component
  // strings — TablePagination's "1-3 of 3" in particular — come from the theme,
  // not from a DataGrid or picker localeText. Without it a list screen renders
  // Turkish headers with English pagination.
  return createTheme(
    {
      palette: {
        mode,
        ...palette,
        grey,
        common,
        divider,
        text: isLight
          ? { primary: grey[800], secondary: grey[600], disabled: grey[500] }
          : { primary: common.white, secondary: grey[500], disabled: grey[600] },
        background: isLight
          ? { paper: common.white, default: common.white, neutral: grey[200] }
          : { paper: grey[800], default: grey[900], neutral: '#28323D' },
        action: {
          active: isLight ? grey[600] : grey[500],
          hover: alpha(GREY_CHANNEL_500, 0.08),
          selected: alpha(GREY_CHANNEL_500, 0.16),
          focus: alpha(GREY_CHANNEL_500, 0.24),
          disabled: alpha(GREY_CHANNEL_500, 0.8),
          disabledBackground: alpha(GREY_CHANNEL_500, 0.24),
          hoverOpacity: 0.08,
          selectedOpacity: 0.08,
          focusOpacity: 0.12,
          activatedOpacity: 0.12,
          disabledOpacity: 0.48,
        },
      },
      shape: { borderRadius: 8 },
      typography: {
        fontFamily: fontFamily.primary,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: { fontWeight: 800, lineHeight: 1.25, fontSize: '2.5rem' },
        h2: { fontWeight: 800, lineHeight: 1.33, fontSize: '2rem' },
        h3: { fontWeight: 700, lineHeight: 1.5, fontSize: '1.5rem' },
        h4: { fontWeight: 700, lineHeight: 1.5, fontSize: '1.25rem' },
        h5: { fontWeight: 700, lineHeight: 1.5, fontSize: '1.125rem' },
        h6: { fontWeight: 600, lineHeight: 1.55, fontSize: '1.0625rem' },
        subtitle1: { fontWeight: 600, lineHeight: 1.5, fontSize: '1rem' },
        subtitle2: { fontWeight: 600, lineHeight: 1.57, fontSize: '0.875rem' },
        body1: { lineHeight: 1.5, fontSize: '1rem' },
        body2: { lineHeight: 1.57, fontSize: '0.875rem' },
        caption: { lineHeight: 1.5, fontSize: '0.75rem' },
        button: { fontWeight: 700, lineHeight: 1.7, fontSize: '0.875rem', textTransform: 'none' },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: { height: '100%', WebkitFontSmoothing: 'antialiased' },
            body: { height: '100%' },
            '#root': { height: '100%' },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: { root: { borderRadius: 8, paddingInline: 16 } },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              boxShadow: `0 0 2px 0 ${alpha(GREY_CHANNEL_500, 0.2)}, 0 12px 24px -4px ${alpha(GREY_CHANNEL_500, 0.12)}`,
            },
          },
        },
        MuiPaper: { defaultProps: { elevation: 0 } },
        MuiTextField: { defaultProps: { variant: 'outlined', size: 'medium' } },
        MuiTableContainer: { styleOverrides: { root: { position: 'relative' } } },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: alpha(PRIMARY_CHANNEL_DARK, 0.04),
                '&:hover': { backgroundColor: alpha(PRIMARY_CHANNEL_DARK, 0.08) },
              },
              '&:last-of-type .MuiTableCell-root': { border: 0 },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: { borderBottomColor: divider, borderBottomStyle: 'dashed' },
            head: {
              fontSize: '0.875rem',
              fontWeight: 600,
              color: isLight ? grey[600] : grey[500],
              backgroundColor: isLight ? grey[200] : '#28323D',
            },
            paddingCheckbox: { paddingLeft: 8 },
          },
        },
        MuiTablePagination: {
          defaultProps: { slotProps: { select: { name: 'table-pagination-select' } } },
          styleOverrides: {
            root: { width: '100%' },
            toolbar: { height: 64 },
            actions: { marginRight: 8 },
            select: { display: 'flex', alignItems: 'center' },
            selectIcon: { right: 4, width: 16, height: 16, top: 'calc(50% - 8px)' },
          },
        },
        MuiTabs: {
          defaultProps: {
            variant: 'scrollable',
            textColor: 'inherit',
            allowScrollButtonsMobile: true,
          },
          styleOverrides: {
            indicator: { backgroundColor: 'currentColor' },
            list: { gap: 40, '@media (max-width:599.95px)': { gap: 24 } },
          },
        },
        MuiTab: {
          defaultProps: { disableRipple: true, iconPosition: 'end' },
          styleOverrides: {
            root: {
              opacity: 1,
              minWidth: 48,
              minHeight: 48,
              paddingLeft: 0,
              paddingRight: 0,
              fontWeight: 500,
              color: isLight ? grey[600] : grey[500],
              '&.Mui-selected': { fontWeight: 600 },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              variants: [
                {
                  props: { variant: 'soft' as const, color: 'default' as const },
                  style: {
                    color: isLight ? grey[800] : common.white,
                    backgroundColor: alpha(GREY_CHANNEL_500, 0.16),
                  },
                },
                ...SOFT_CHIP_COLORS.map((colorKey) => ({
                  props: { variant: 'soft' as const, color: colorKey },
                  style: {
                    color: isLight ? palette[colorKey].dark : palette[colorKey].light,
                    backgroundColor: alphaHex(palette[colorKey].main, 0.16),
                  },
                })),
              ],
            },
            deleteIcon: { opacity: 0.48, '&:hover': { opacity: 1, color: 'inherit' } },
          },
        },
        MuiLink: { defaultProps: { underline: 'hover' } },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              gap: 8,
              minWidth: 'var(--size)',
              minHeight: 'var(--size)',
              padding: 'var(--padding)',
              fontWeight: 600,
              variants: [
                ...TOGGLE_BUTTON_COLORS.map((colorKey) => ({
                  props: { color: colorKey },
                  style: {
                    '&:hover': {
                      borderColor: alphaHex(palette[colorKey].main, 0.48),
                      backgroundColor: alphaHex(palette[colorKey].main, 0.08),
                    },
                  },
                })),
                ...(['small', 'medium', 'large'] as const).map((size) => ({
                  props: { size },
                  style: TOGGLE_BUTTON_SIZES[size],
                })),
                {
                  props: {},
                  style: {
                    '&.Mui-selected': {
                      borderColor: 'currentColor',
                      boxShadow: '0 0 0 0.75px currentColor',
                    },
                    '&.Mui-disabled': {
                      boxShadow: 'none',
                      color: alpha(GREY_CHANNEL_500, 0.8),
                      borderColor: alpha(GREY_CHANNEL_500, 0.24),
                      '&.Mui-selected': { backgroundColor: alpha(GREY_CHANNEL_500, 0.24) },
                    },
                  },
                },
              ],
            },
          },
        },
        MuiToggleButtonGroup: {
          styleOverrides: {
            root: {
              ...TOGGLE_BUTTON_GROUP_GAP,
              gap: 'var(--group-gap)',
              padding: 'var(--group-gap)',
              border: `1px solid ${alpha(GREY_CHANNEL_500, 0.16)}`,
            },
            grouped: {
              border: 'none',
              borderRadius: 'inherit',
              padding: 'calc(var(--padding) - var(--group-gap))',
              minWidth: 'calc(var(--size) - (var(--group-gap) * 2 + 2px))',
              minHeight: 'calc(var(--size) - (var(--group-gap) * 2 + 2px))',
              variants: [
                {
                  props: {},
                  style: {
                    '&.Mui-selected': { boxShadow: 'none' },
                    '&.Mui-disabled': { border: 'none' },
                  },
                },
              ],
            },
          },
        },
      },
    },
    trTR,
  );
}
